import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Edit, Plus, RefreshCw } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { PackZoneAdmin, CreatePackZoneAdminRequest } from '../../../../types/packZone';
import { packZoneService } from '../../../../services/packZoneService';
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils';
import type { ColumnDefinition, TableAction } from '../../../shared/table/types/table';
import { PackZoneBulkUpdateModal } from './PackZoneBulkUpdateModal';
import { PrinterSelect } from '../../../common/PrinterSelect';
import printNodeService from '../../../../services/printNodeService';
import type { Printer } from '../../../../types/printNode';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';

/**
 * Pakkesone beriket med UNC-formattert printer-tekst for visning i tabellen.
 * DB lagrer fortsatt PrintNode-ID i xxxPrinterName-feltene; display-feltene er
 * kun for rendering, sortering, filtrering og CSV-eksport.
 */
interface PackZoneAdminDisplay extends PackZoneAdmin {
  packageSlipPrinterDisplay: string;
  shippingLabelsPrinterDisplay: string;
  consignmentNotePrinterDisplay: string;
}

/**
 * Bygger map: PrintNode-printerID (som streng) -> printer.name.
 * printer.name inneholder allerede full UNC-sti i Windows-miljø
 * (f.eks. "\\sr-prn01.res.bitpro.as\sr-sog-zebra01"), så vi bruker
 * verdien direkte — samme som PrinterSelect-dropdown gjør.
 */
const buildPrinterIdToNameMap = (printers: Printer[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const p of printers) {
    map.set(String(p.id), p.name);
  }
  return map;
};

/**
 * Slår opp printer-tekst for visning. Lagret verdi kan være:
 *  - Numerisk PrintNode-ID (nytt format) -> returner printer.name hvis funnet;
 *    ellers merkes ID-en som ugyldig NÅR skriverlista faktisk er lastet (TW 36370528 —
 *    en råtten ID viste seg tidligere som et nakent tall og feilet først ved utskrift)
 *  - Gammelt printernavn (før migrering) -> returner som den er
 *  - Tom/null -> tom streng
 */
const resolvePrinterDisplay = (
  rawValue: string | null,
  printerNameMap: Map<string, string>
): string => {
  if (!rawValue) return '';
  const trimmed = rawValue.trim();
  if (!trimmed) return '';
  if (/^\d+$/.test(trimmed)) {
    const name = printerNameMap.get(trimmed);
    if (name) return name;
    // Tom map = lista er ikke lastet (PrintNode nede?) — kan ikke avgjøre gyldighet
    return printerNameMap.size > 0 ? `⚠ ${trimmed} (finnes ikke i PrintNode)` : trimmed;
  }
  return trimmed;
};

interface PackZoneAppProps {
  onClose?: () => void;
  onBack?: () => void;
}

interface PackZoneFormData {
  packZoneID: string;
  packZoneDescription: string;
  packageSlipPrinterName: string;
  shippingLabelsPrinterName: string;
  consignmentNotePrinterName: string;
  printerID: string;
  packageSlipNumOfCopies: string;
  labelExportFolder: string;
}

const emptyFormData: PackZoneFormData = {
  packZoneID: '',
  packZoneDescription: '',
  packageSlipPrinterName: '',
  shippingLabelsPrinterName: '',
  consignmentNotePrinterName: '',
  printerID: '',
  packageSlipNumOfCopies: '',
  labelExportFolder: ''
};

export const PackZoneApp: React.FC<PackZoneAppProps> = ({ onClose, onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [packZones, setPackZones] = useState<PackZoneAdmin[]>([]);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<PackZoneFormData>(emptyFormData);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadPackZones();
  }, []);

  // Last PrintNode-printere parallelt for å berike visningen med UNC-sti.
  // Feiler stille — fallback er at PrintNode-ID vises som tekst i tabellen.
  useEffect(() => {
    if (!user?.companyNo) return;
    let cancelled = false;
    printNodeService.getPrinters(user.companyNo, 'tenant')
      .then(list => { if (!cancelled) setPrinters(list); })
      .catch(err => console.error('Kunne ikke laste PrintNode-printere for visning:', err));
    return () => { cancelled = true; };
  }, [user?.companyNo]);

  const printerIdToName = useMemo(() => buildPrinterIdToNameMap(printers), [printers]);

  const displayPackZones: PackZoneAdminDisplay[] = useMemo(
    () => packZones.map(z => ({
      ...z,
      packageSlipPrinterDisplay: resolvePrinterDisplay(z.packageSlipPrinterName, printerIdToName),
      shippingLabelsPrinterDisplay: resolvePrinterDisplay(z.shippingLabelsPrinterName, printerIdToName),
      consignmentNotePrinterDisplay: resolvePrinterDisplay(z.consignmentNotePrinterName, printerIdToName),
    })),
    [packZones, printerIdToName]
  );

  const loadPackZones = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await packZoneService.getPackZones();
      setPackZones(data);
    } catch (err) {
      console.error('Error loading pack zones:', err);
      setError(t('manager.modules.adminDelivery.packZone.errors.loadFailed', {}, 'Feil ved lasting av pakkesoner'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Selection handling
  const handleSelectionChange = useCallback((ids: (string | number)[], _data: PackZoneAdminDisplay[]) => {
    setSelectedIds(ids.map(id => String(id)));
  }, []);

  const getRowId = useCallback((item: PackZoneAdminDisplay) => item.packZoneID, []);

  // Handle bulk edit
  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert(t('manager.modules.adminDelivery.packZone.errors.selectAtLeastOne', {}, 'Velg minst én pakkesone for vedlikehold'));
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length, t]);

  // Column factory
  const columnFactory = createColumnFactory<PackZoneAdminDisplay>();

  // Column definitions - read-only, no inline editing.
  // Skriver-kolonnene bruker *PrinterDisplay-feltene som inneholder UNC-sti
  // (f.eks. \\hostname\printername). PrintNode-IDen forblir lagret i
  // *PrinterName-feltene i DB og brukes av PrinterSelect ved redigering.
  const columns: ColumnDefinition<PackZoneAdminDisplay>[] = useMemo(() => [
    columnFactory.createTextColumn('packZoneID', 'packZoneID', t('manager.modules.adminDelivery.packZone.columns.packZoneID', {}, 'Pakkesone ID'), {
      width: 140,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('packZoneDescription', 'packZoneDescription', t('common.fields.description', {}, 'Beskrivelse'), {
      width: 220,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('packageSlipPrinterDisplay', 'packageSlipPrinterDisplay', t('manager.modules.adminDelivery.packZone.fields.packageSlipPrinter', {}, 'Pakkseddel-skriver'), {
      width: 240,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('shippingLabelsPrinterDisplay', 'shippingLabelsPrinterDisplay', t('manager.modules.adminDelivery.packZone.fields.shippingLabelsPrinter', {}, 'Fraktetikett-skriver'), {
      width: 240,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('consignmentNotePrinterDisplay', 'consignmentNotePrinterDisplay', t('manager.modules.adminDelivery.packZone.fields.consignmentNotePrinter', {}, 'Fraktbrev-skriver'), {
      width: 240,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('printerID', 'printerID', t('manager.modules.adminDelivery.packZone.fields.printerID', {}, 'PrinterID fileksport(transport)'), {
      width: 200,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createNumberColumn('packageSlipNumOfCopies', 'packageSlipNumOfCopies', t('manager.modules.adminDelivery.packZone.fields.packageSlipNumOfCopies', {}, 'Ant. kopier pakkseddel'), {
      width: 160,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('labelExportFolder', 'labelExportFolder', t('manager.modules.adminDelivery.packZone.fields.labelExportFolder', {}, 'Eksportmappe'), {
      width: 200,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('createdBy', 'createdBy', t('manager.modules.adminDelivery.common.columns.createdBy', {}, 'Opprettet av'), {
      width: 120,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createDateColumn('created', 'created', t('manager.modules.adminDelivery.common.columns.created', {}, 'Opprettet'), {
      width: 130,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('updatedBy', 'updatedBy', t('manager.modules.adminDelivery.common.columns.updatedBy', {}, 'Oppdatert av'), {
      width: 120,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createDateColumn('lastUpdate', 'lastUpdate', t('manager.modules.adminDelivery.common.columns.lastUpdate', {}, 'Sist oppdatert'), {
      width: 130,
      sortable: true,
      filterType: 'text'
    })
  ], [t]);

  // Table actions
  const tableActions: TableAction<PackZoneAdminDisplay>[] = useMemo(() => [
    {
      id: 'create-packzone',
      label: t('manager.modules.adminDelivery.packZone.actions.createNew', {}, 'Opprett ny'),
      icon: Plus,
      onClick: () => {
        setFormData(emptyFormData);
        setFormError(null);
        setSuccessMessage(null);
        setShowCreateModal(true);
      },
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: t('manager.modules.adminDelivery.common.maintain', { '0': selectedIds.length }, `Vedlikehold (${selectedIds.length})`),
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: (_data: PackZoneAdminDisplay[], _selected?: PackZoneAdminDisplay[]) => selectedIds.length === 0,
      variant: 'primary' as const
    },
    {
      id: 'refresh-packzones',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => loadPackZones(),
      variant: 'secondary' as const
    }
  ], [selectedIds.length, handleBulkEdit, loadPackZones, t]);

  // Create form handlers
  const handleInputChange = (field: keyof PackZoneFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.packZoneID.trim()) {
      setFormError(t('manager.modules.adminDelivery.packZone.errors.idRequired', {}, 'Pakkesone ID er påkrevd'));
      return;
    }
    const duplicate = packZones.find(z =>
      z.packZoneID.toLowerCase() === formData.packZoneID.trim().toLowerCase()
    );
    if (duplicate) {
      setFormError(t('manager.modules.adminDelivery.packZone.errors.idExists', {}, 'Pakkesone ID finnes allerede'));
      return;
    }

    setFormLoading(true);
    setFormError(null);

    try {
      const numOrNull = (val: string) => val.trim() ? parseInt(val.trim(), 10) : undefined;

      const createRequest: CreatePackZoneAdminRequest = {
        packZoneID: formData.packZoneID.trim(),
        packZoneDescription: formData.packZoneDescription.trim() || undefined,
        packageSlipPrinterName: formData.packageSlipPrinterName.trim() || undefined,
        shippingLabelsPrinterName: formData.shippingLabelsPrinterName.trim() || undefined,
        consignmentNotePrinterName: formData.consignmentNotePrinterName.trim() || undefined,
        printerID: formData.printerID.trim() || undefined,
        packageSlipNumOfCopies: numOrNull(formData.packageSlipNumOfCopies),
        labelExportFolder: formData.labelExportFolder.trim() || undefined
      };

      await packZoneService.createPackZone(createRequest);
      setSuccessMessage(t('manager.modules.adminDelivery.packZone.create.success', {}, 'Ny pakkesone ble opprettet'));
      setTimeout(() => {
        loadPackZones();
        setShowCreateModal(false);
        setSuccessMessage(null);
      }, 1000);
    } catch (err) {
      console.error('Error creating pack zone:', err);
      setFormError(err instanceof Error ? err.message : t('manager.modules.adminDelivery.packZone.errors.createFailed', {}, 'Feil ved opprettelse av pakkesone'));
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-white border-b">
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack || (() => {})} />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{t('manager.modules.adminDelivery.packZone.title', {}, 'Pakkesoner')}</h1>
            <p className="text-sm text-gray-600 mt-1">
              {t('manager.modules.adminDelivery.packZone.subtitle', {}, 'Administrer pakkesoner og skriverinnstillinger')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <button
              onClick={onClose}
              className="px-3 py-2 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              {t('common.buttons.close', {}, 'Lukk')}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={displayPackZones}
          columns={columns}
          loading={loading}
          error={error || undefined}
          config={{
            persistenceKey: 'packzone-table',
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 100,
            compactMode: true,
            stickyHeader: true,
            allowColumnReorder: true,
            allowColumnResize: true,
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'current-page'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={handleSelectionChange}
          getRowId={getRowId}
          tableActions={tableActions}
          aria-label={t('manager.modules.adminDelivery.packZone.tableAriaLabel', {}, 'Pakkesoner tabell')}
        />
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && selectedIds.length > 0 && (
        <PackZoneBulkUpdateModal
          isOpen={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          selectedIds={selectedIds}
          selectedData={packZones.filter(z => selectedIds.includes(z.packZoneID))}
          onUpdateSuccess={() => {
            loadPackZones();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
          onDeleteSuccess={() => {
            loadPackZones();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">{t('manager.modules.adminDelivery.packZone.create.title', {}, 'Opprett ny pakkesone')}</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 text-xl" aria-label={t('manager.modules.adminDelivery.common.closeModal', {}, 'Lukk modal')}>×</button>
            </div>

            <form onSubmit={handleCreateSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">{formError}</p>
                </div>
              )}
              {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-800 text-sm">{successMessage}</p>
                </div>
              )}

              {/* Grunndata */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('manager.modules.adminDelivery.packZone.fields.packZoneIDRequired', {}, 'Pakkesone ID *')}</label>
                  <input type="text" value={formData.packZoneID} onChange={(e) => handleInputChange('packZoneID', e.target.value)} disabled={formLoading} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50" placeholder={t('manager.modules.adminDelivery.packZone.columns.packZoneID', {}, 'Pakkesone ID')} maxLength={50} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.fields.description', {}, 'Beskrivelse')}</label>
                  <input type="text" value={formData.packZoneDescription} onChange={(e) => handleInputChange('packZoneDescription', e.target.value)} disabled={formLoading} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50" placeholder={t('common.fields.description', {}, 'Beskrivelse')} maxLength={255} />
                </div>
              </div>

              {/* Skrivere med PrinterSelect.
                  Bruker valueField="id" for å lagre numerisk PrintNode-ID istedenfor navn —
                  konsistent med SystemSetup og Profile. Eliminerer tvetydighet når samme
                  printernavn finnes på flere PrintNode-computere. Eksisterende konfig med
                  navn-verdier vil vises som tom dropdown og må re-velges av admin. */}
              <div className="space-y-3">
                <PrinterSelect
                  scope="tenant"
                  label={t('manager.modules.adminDelivery.packZone.fields.packageSlipPrinter', {}, 'Pakkseddel-skriver')}
                  value={formData.packageSlipPrinterName}
                  onChange={(v) => handleInputChange('packageSlipPrinterName', v || '')}
                  valueField="id"
                  disabled={formLoading}
                  showTestButton={true}
                  size="sm"
                />
                <PrinterSelect
                  scope="tenant"
                  label={t('manager.modules.adminDelivery.packZone.fields.shippingLabelsPrinter', {}, 'Fraktetikett-skriver')}
                  value={formData.shippingLabelsPrinterName}
                  onChange={(v) => handleInputChange('shippingLabelsPrinterName', v || '')}
                  valueField="id"
                  disabled={formLoading}
                  showTestButton={true}
                  size="sm"
                />
                <PrinterSelect
                  scope="tenant"
                  label={t('manager.modules.adminDelivery.packZone.fields.consignmentNotePrinter', {}, 'Fraktbrev-skriver')}
                  value={formData.consignmentNotePrinterName}
                  onChange={(v) => handleInputChange('consignmentNotePrinterName', v || '')}
                  valueField="id"
                  disabled={formLoading}
                  showTestButton={true}
                  size="sm"
                />
              </div>

              {/* Øvrige felt */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('manager.modules.adminDelivery.packZone.fields.printerID', {}, 'PrinterID fileksport(transport)')}</label>
                  <input type="text" value={formData.printerID} onChange={(e) => handleInputChange('printerID', e.target.value)} disabled={formLoading} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50" placeholder={t('manager.modules.adminDelivery.packZone.placeholders.printerID', {}, 'PrinterID')} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('manager.modules.adminDelivery.packZone.fields.packageSlipNumOfCopies', {}, 'Ant. kopier pakkseddel')}</label>
                  <input type="number" value={formData.packageSlipNumOfCopies} onChange={(e) => handleInputChange('packageSlipNumOfCopies', e.target.value)} disabled={formLoading} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50" min={0} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('manager.modules.adminDelivery.packZone.fields.labelExportFolder', {}, 'Eksportmappe')}</label>
                  <input type="text" value={formData.labelExportFolder} onChange={(e) => handleInputChange('labelExportFolder', e.target.value)} disabled={formLoading} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50" placeholder={t('manager.modules.adminDelivery.packZone.placeholders.exportFolder', {}, 'F.eks. C:\\BizClickExport\\Labels')} />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowCreateModal(false)} disabled={formLoading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50">{t('common.buttons.cancel', {}, 'Avbryt')}</button>
                <button type="submit" disabled={formLoading} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {formLoading ? t('manager.modules.adminDelivery.common.creating', {}, 'Oppretter...') : t('manager.modules.adminDelivery.packZone.create.submit', {}, 'Opprett pakkesone')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
